import _ from 'lodash';

import {
  createFetchActionForNamespace,
  createPostActionForNamespace,
  createReducer,
  createFetchReducers,
  createFetchSelectors,
  defaultFailureReducer
} from 'utils/reducers';

const createFetchAction = createFetchActionForNamespace('bookings/detail');
const createPostAction = createPostActionForNamespace('bookings/detail');

export const REFETCH_AGREEMENT_DELAY = 5000;

const RESET_BOOKING_DETAIL_STATE = 'bookings/detail/RESET_BOOKING_DETAIL_STATE';

export const fetchBooking = createFetchAction({
  name: 'FETCH_BOOKING',
  initialAction: ({locationRef, bookingId}, extraActions = []) => ({
    payload: {
      locationRef,
      bookingId,
      extraActions
    }
  })
});

export const selectBooking = state => {
  return _.get(state, 'bookings.detail.booking');
};

export const selectBookingNotFound = state => {
  return _.get(state, 'bookings.detail.notFound', false);
};

export const selectOffers = state => {
  const {offers} = state.bookings.detail;
  return offers.data;
};

export const selectGuarantor = state => {
  const booking = selectBooking(state);

  return _.get(booking, 'guarantor');
};

export const selectAgreementUser = (state, agreementUserId) => {
  const booking = selectBooking(state);

  if (_.isNil(booking)) {
    return;
  }

  if (_.get(selectGuarantor(state), 'id') === agreementUserId) {
    return booking.guarantor;
  }

  const tenants = _.get(booking, 'tenants', []);
  return _.find(tenants, tenant => tenant.agreementUserId === agreementUserId);
};

export const resetBookingDetailState = () => ({
  type: RESET_BOOKING_DETAIL_STATE,
  payload: {}
});

export const fetchLegacyTypes = createFetchAction({
  name: 'FETCH_LEGACY_TYPES',
  initialAction: (extraActions = []) => ({payload: {extraActions}})
});

export const createLegacyDocument = createPostAction({
  name: 'CREATE_LEGACY_DOCUMENT',
  initialAction: (
    {identifier, legacyType, title, notes, booking},
    extraActions = []
  ) => ({
    payload: {identifier, legacyType, title, notes, booking, extraActions}
  }),
  formName: 'legacyDocumentUpload'
});

export const createOneOffChargeInvoiceAndPayment = createPostAction({
  name: 'CREATE_ONE_OFF_CHARGE_INOVOICE_AND_PAYMENT',
  initialAction: (
    {
      bookingId,
      label,
      description,
      amount,
      createPayment,
      paymentMethod,
      paymentDate,
      invoiceDueDate,
      reference,
      xeroAccount,
      batchNumber,
      creditCardNumber,
      oneOffChargeReasonId
    },
    extraActions = []
  ) => ({
    payload: {
      bookingId,
      label,
      description,
      amount,
      createPayment,
      paymentMethod,
      paymentDate,
      invoiceDueDate,
      reference,
      xeroAccount,
      batchNumber,
      creditCardNumber,
      oneOffChargeReasonId,
      extraActions
    }
  }),
  formName: 'createOneOffChargeInvoiceAndPayment'
});

export const verifyKycDocumentsUpload = createPostAction({
  name: 'VERIFY_KYC_DOCUMENTS_UPLOAD',
  initialAction: ({poid, poa, visa, kyc}, extraActions = []) => ({
    payload: {poid, poa, visa, kyc, extraActions}
  }),
  formName: 'kycDocumentUpload'
});

export const deleteKycDocument = createPostAction({
  name: 'DELETE_KYC_DOCUMENT',
  initialAction: ({kycDocumentId, kyc}, extraActions = []) => ({
    payload: {kycDocumentId, kyc, extraActions}
  })
});

export const fetchAgreements = createFetchAction({
  name: 'FETCH_AGREEMENTS',
  initialAction: ({bookingId}) => ({
    payload: {bookingId}
  }),
  stateKey: 'agreements',
  defaultStateValue: []
});

export const selectAgreements = createFetchSelectors(fetchAgreements).data;

export const fetchAgreement = createFetchAction({
  name: 'FETCH_AGREEMENT',
  initialAction: ({agreementId}, extraActions = []) => ({
    payload: {
      agreementId,
      extraActions
    }
  })
});

export const fetchSendAgreementReminderDetails = createFetchAction({
  name: 'FETCH_SEND_AGREEMENT_REMINDER_DETAILS',
  initialAction: ({agreement}, extraActions = []) => ({
    payload: {
      agreement,
      extraActions
    }
  })
});

export const fetchSendAgreementDetails = createFetchAction({
  name: 'FETCH_SEND_AGREEMENT_DETAILS',
  initialAction: ({agreementId}, extraActions = []) => ({
    payload: {agreementId, extraActions}
  })
});

export const sendAgreement = createPostAction({
  name: 'SEND_AGREEMENT',
  initialAction: (
    {agreementId, warnings},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      agreementId,
      warnings,
      successExtraActions,
      failureExtraActions
    }
  })
});

export const sendAgreementReminder = createPostAction({
  name: 'SEND_AGREEMENT_REMINDER',
  initialAction: ({agreement, agreementUsers}, extraActions = []) => ({
    payload: {agreement, agreementUsers, extraActions}
  }),
  formName: 'sendAgreementReminder'
});

export const fetchAgreementTemplates = createFetchAction({
  name: 'FETCH_AGREEMENT_TEMPLATES',
  initialAction: ({bookingId}, extraActions = []) => ({
    payload: {
      bookingId,
      extraActions
    }
  })
});

export const addAgreement = createPostAction({
  name: 'ADD_AGREEMENT',
  initialAction: (
    {agreementTemplate, locationRef, bookingId},
    extraActions = []
  ) => ({
    payload: {agreementTemplate, locationRef, bookingId, extraActions}
  }),
  formName: 'addAgreement'
});

export const createOffer = createPostAction({
  name: 'CREATE_OFFER',
  initialAction: (
    {startDate, endDate, roomDeposit, pwRent, breakClause, booking},
    extraActions = []
  ) => ({
    payload: {
      startDate,
      endDate,
      roomDeposit,
      pwRent,
      breakClause,
      booking,
      extraActions
    }
  }),
  formName: 'createBookingOffer'
});

export const cancelAgreement = createPostAction({
  name: 'CANCEL_AGREEMENT',
  initialAction: (
    {agreement},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {agreement, successExtraActions, failureExtraActions}
  })
});

export const signAgreementManually = createPostAction({
  name: 'SIGN_AGREEMENT_MANUALLY',
  initialAction: (
    {agreement},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {agreement, successExtraActions, failureExtraActions}
  })
});

export const fetchRentSchedule = createFetchAction({
  name: 'FETCH_RENT_SCHEDULE',
  initialAction: ({bookingId}) => ({
    payload: {bookingId}
  }),
  stateKey: 'rentSchedule',
  defaultStateValue: []
});

export const selectRentSchedule = createFetchSelectors(fetchRentSchedule).data;

export const fetchKycs = createFetchAction({
  name: 'FETCH_KYCS',
  initialAction: ({bookingId}) => ({
    payload: {bookingId}
  }),
  stateKey: 'kycs',
  defaultStateValue: []
});

export const selectKycs = createFetchSelectors(fetchKycs).data;

export const fetchAffordabilities = createFetchAction({
  name: 'FETCH_AFFORDABILITIES',
  initialAction: ({bookingId}) => ({
    payload: {bookingId}
  }),
  stateKey: 'affordabilities',
  defaultStateValue: []
});

export const selectAffordabilities = createFetchSelectors(fetchAffordabilities)
  .data;

export const fetchLegacyDocuments = createFetchAction({
  name: 'FETCH_LEGACY_DOCUMENTS',
  initialAction: ({bookingId}) => ({payload: {bookingId}}),
  stateKey: 'legacyDocuments',
  defaultStateValue: []
});

export const selectLegacyDocuments = createFetchSelectors(fetchLegacyDocuments)
  .data;

export const fetchAffordability = createFetchAction({
  name: 'FETCH_AFFORDABILITY',
  initialAction: ({bookingId, affordabilityId}, extraActions = []) => ({
    payload: {
      bookingId,
      affordabilityId,
      extraActions
    }
  })
});

export const verifyAffordabilityDocumentUpload = createPostAction({
  name: 'VERIFY_AFFORDABILITY_DOCUMENT_UPLOAD',
  initialAction: ({identifier, bookingId, affordabilityId}, extraActions) => ({
    payload: {identifier, bookingId, affordabilityId, extraActions}
  }),
  formName: 'affordabilityDocumentUpload'
});

export const searchTenants = createPostAction({
  name: 'SEARCH_TENANTS',
  initialAction: ({value}, extraActions = []) => ({
    payload: {value, extraActions}
  })
});

export const verifyKyc = createPostAction({
  name: 'VERIFY_KYC',
  initialAction: (
    {bookingId, kycId},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {bookingId, kycId, successExtraActions, failureExtraActions}
  })
});

export const verifyAddress = createPostAction({
  name: 'VERIFY_ADDRESS',
  initialAction: (
    {bookingId, addressHistoryId, kycId},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      bookingId,
      kycId,
      addressHistoryId,
      successExtraActions,
      failureExtraActions
    }
  })
});

export const verifyPOID = createPostAction({
  name: 'VERIFY_POID',
  initialAction: (
    {bookingId, kycId},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {bookingId, kycId, successExtraActions, failureExtraActions}
  })
});

export const verifyVisa = createPostAction({
  name: 'VERIFY_VISA',
  initialAction: ({bookingId, kycId, expiryDate}, extraActions = []) => ({
    payload: {bookingId, kycId, expiryDate, extraActions}
  }),
  formName: 'kycVerifyVisaForm'
});

export const fetchKycDetails = createFetchAction({
  name: 'FETCH_KYC_DETAILS',
  initialAction: ({kycId, bookingId}, extraActions = []) => ({
    payload: {kycId, bookingId, extraActions}
  }),
  stateKey: 'selectedKyc',
  defaultStateValue: {}
});

const selectKyc = createFetchSelectors(fetchKycDetails).data;
export const selectSelectedKycId = state => _.get(selectKyc(state), 'id');

export const fetchKycSectionDetails = createFetchAction({
  name: 'FETCH_KYC_SECTION_DETAILS',
  initialAction: ({kycId, bookingId, sectionId}, extraActions = []) => ({
    payload: {kycId, bookingId, sectionId, extraActions}
  })
});

export const confirmCurrentAddress = createPostAction({
  name: 'CONFIRM_CURRENT_ADDRESS',
  initialAction: (
    {agreementUserId, addressHistoryId},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      agreementUserId,
      addressHistoryId,
      successExtraActions,
      failureExtraActions
    }
  })
});

export const generateRentSchedule = createPostAction({
  name: 'GENERATE_RENT_SCHEDULE',
  initialAction: (
    {
      booking,
      billingDate,
      billingFrequency,
      invoicesToChargeBeforeMoveIn,
      preArrivalInvoicesDue,
      rentPerMonth
    },
    extraActions = []
  ) => ({
    payload: {
      booking,
      billingDate,
      billingFrequency,
      invoicesToChargeBeforeMoveIn,
      preArrivalInvoicesDue,
      rentPerMonth,
      extraActions
    }
  }),
  formName: 'createRentSchedule'
});

export const requestKycDocument = createPostAction({
  name: 'REQUEST_KYC_DOCUMENT',
  initialAction: (
    {title, message, kycSection, kycId, bookingId},
    extraActions = []
  ) => ({
    payload: {title, message, kycSection, kycId, bookingId, extraActions}
  }),
  formName: 'requestKycDocument'
});

export const applyRentSchedule = createPostAction({
  name: 'APPLY_RENT_SCHEDULE',
  initialAction: (
    {
      booking,
      billingDate,
      billingFrequency,
      invoicesToChargeBeforeMoveIn,
      preArrivalInvoicesDue,
      rentPerMonth
    },
    extraActions = []
  ) => ({
    payload: {
      booking,
      billingDate,
      billingFrequency,
      invoicesToChargeBeforeMoveIn,
      preArrivalInvoicesDue,
      rentPerMonth,
      extraActions
    }
  }),
  formName: 'createRentSchedule'
});

export const fetchRentScheduleSummary = createFetchAction({
  name: 'FETCH_RENT_SCHEDULE_SUMMARY',
  initialAction: (
    {bookingId, rentFrequency, rentPerMonth},
    extraActions = []
  ) => ({
    payload: {
      bookingId,
      rentFrequency,
      rentPerMonth,
      extraActions
    }
  })
});

export const addGuarantor = createPostAction({
  name: 'ADD_GUARANTOR',
  initialAction: ({bookingId, fullName, email}, extraActions = []) => ({
    payload: {bookingId, fullName, email, extraActions}
  }),
  formName: 'addGuarantorForm'
});

export const addSecondaryTenant = createPostAction({
  name: 'ADD_SECONDARY_TENANT',
  initialAction: ({bookingId, fullName, email}, extraActions = []) => ({
    payload: {bookingId, fullName, email, extraActions}
  }),
  formName: 'addSecondaryTenantForm'
});

export const removeSecondaryTenant = createPostAction({
  name: 'REMOVE_SECONDARY_TENANT',
  initialAction: (
    {bookingId},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {bookingId, successExtraActions, failureExtraActions}
  })
});

export const removeGuarantor = createPostAction({
  name: 'REMOVE_GUARANTOR',
  initialAction: (
    {bookingId},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {bookingId, successExtraActions, failureExtraActions}
  })
});

export const passAffordability = createPostAction({
  name: 'PASS_AFFORDABILITY',
  initialAction: (
    {locationRef, bookingId},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {locationRef, bookingId, successExtraActions, failureExtraActions}
  })
});

export const deleteAffordabilityDocument = createPostAction({
  name: 'DELETE_AFFORDABILITY_DOCUMENT',
  initialAction: ({documentId}, extraActions = []) => ({
    payload: {documentId, extraActions}
  })
});

export const addEmployment = createPostAction({
  name: 'ADD_EMPLOYMENT',
  initialAction: (
    {
      agreementUserId,
      unemployed,
      name,
      title,
      website,
      salary,
      startDate,
      affordabilityId,
      bookingId
    },
    extraActions = []
  ) => ({
    payload: {
      agreementUserId,
      unemployed,
      name,
      title,
      website,
      salary,
      startDate,
      affordabilityId,
      bookingId,
      extraActions
    }
  }),
  formName: 'addEmployment'
});

export const cancelBooking = createPostAction({
  name: 'CANCEL_BOOKING',
  initialAction: ({bookingId, cancelledReason}, extraActions = []) => ({
    payload: {bookingId, cancelledReason, extraActions}
  }),
  formName: 'cancelBooking'
});

export const deleteLegacyDocument = createPostAction({
  name: 'DELETE_LEGACY_DOCUMENT',
  initialAction: (
    {legacyDocumentId},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      legacyDocumentId,
      successExtraActions,
      failureExtraActions
    }
  })
});

export const editLegacyDocument = createPostAction({
  name: 'EDIT_LEGACY_DOCUMENT',
  initialAction: (
    {title, legacyType, notes, documentId},
    extraActions = []
  ) => ({
    payload: {
      title,
      legacyType,
      notes,
      documentId,
      extraActions
    }
  }),
  formName: 'legacyDocumentEdit'
});

export const fetchCreateOfferWarnings = createFetchAction({
  name: 'FETCH_CREATE_OFFER_WARNINGS',
  initialAction: ({bookingId}, extraActions) => ({
    payload: {
      bookingId,
      extraActions
    }
  })
});

export const createAddressHistory = createPostAction({
  name: 'CREATE_ADDRESS_HISTORY',
  initialAction: (
    {agreementUserId, street, city, country, state, postalCode, location},
    extraActions = []
  ) => ({
    payload: {
      agreementUserId,
      street,
      city,
      country,
      state,
      postalCode,
      location,
      extraActions
    }
  }),
  formName: 'addressHistory'
});

export const fetchBookingActivityLog = createFetchAction({
  name: 'FETCH_BOOKING_ACTIVITY_LOG',
  initialAction: ({bookingId}, extraActions = []) => ({
    payload: {bookingId, extraActions}
  }),
  stateKey: 'activityLog',
  defaultStateValue: []
});

export const selectBookingActivityLog = createFetchSelectors(
  fetchBookingActivityLog
).data;

export const BOOKING_VERIFICATION_METHOD = {
  manual: 'Manual',
  verification_service_v1: 'Verification Service V1'
};

export const INITIAL_BILLING_FREQUENCY = {
  monthly_2_weeks_security: 'Monthly License fee + 2 weeks security bond',
  monthly_2_weeks_security_guarantor:
    'Monthly License Fee + 2 weeks security bond + qualified guarantor',
  full_upfront_2_weeks_security:
    'Full License fee upfront + 2 weeks security bond',
  monthly_5_weeks_security: 'Monthly License Fee + 5 weeks security bond'
};

export const checkOutTenantsEarly = createPostAction({
  name: 'CHECK_OUT_TENANTS_EARLY',
  initialAction: ({bookingId}, extraActions = []) => ({
    payload: {bookingId, extraActions}
  })
});

export const undoCheckOutTenantsEarly = createPostAction({
  name: 'UNDO_CHECK_OUT_TENANTS_EARLY',
  initialAction: ({bookingId}, extraActions = []) => ({
    payload: {bookingId, extraActions}
  })
});

const initialState = {
  loading: true,
  booking: null,
  hasTenancy: false,
  error: null,
  offers: {data: []},
  selectedKyc: {}
};

const fetchBookingSuccessAction = (state, action) => {
  const payload = {...action.payload};
  const {offers} = payload;
  delete payload.offers;

  return {
    ...state,
    loading: false,
    booking: payload,
    hasTenancy: action.payload.tenancy !== null,
    offers: {
      ...state.offers,
      data: offers
    }
  };
};

const fetchBookingNotFoundAction = (state, _action) => {
  return {
    ...state,
    isLoading: false,
    notFound: true
  };
};

const fetchReducers = createReducer(initialState, {
  ...createFetchReducers(
    fetchBooking,
    fetchBookingSuccessAction,
    defaultFailureReducer,
    fetchBookingNotFoundAction
  ),
  ...createFetchReducers(fetchAgreements),
  ...createFetchReducers(fetchKycs),
  ...createFetchReducers(fetchAffordabilities),
  ...createFetchReducers(fetchLegacyDocuments),
  ...createFetchReducers(fetchRentSchedule),
  ...createFetchReducers(fetchKycDetails),
  ...createFetchReducers(fetchBookingActivityLog)
});

const reducer = (state = initialState, action) => {
  const newState = fetchReducers(state, action);

  switch (action.type) {
    case RESET_BOOKING_DETAIL_STATE:
      return initialState;

    default:
      return newState;
  }
};

export default reducer;

export {default as bookingDetailSaga} from './saga';
